/**
 * @typedef PropertiesPanelConfig
 * @type {{
 *   expanded: boolean;
 *   node: WorkflowNode | null;
 *   schema: {data: Object, ui: Object} | null;
 *   disabled: boolean;
 * }}
 */

// Cache to prevent returning new references when nothing meaningful changed
let _prevConfig = null
let _prevNodeUid = null
let _prevExpanded = false
let _prevSchemaData = null
let _prevDisabled = false
let _prevNodeDataForm = null
let _prevNodeDataCases = null
let _prevNodeDataDefaultTarget = null
let _prevNodeDataConditions = null

/**
 * Generates the configuration for the properties panel based on the current selection and nodes.
 * @param {PropertiesPanelState} propertiesPanel - The current properties panel configuration.
 * @param {SelectionState} selection - The current selection containing nodes.
 * @param {NodesType} nodes - The collection of nodes in the workflow.
 * @param {WorkflowMode} mode - The current mode of the application (e.g., 'edit' or 'view').
 * @returns {PropertiesPanelConfig} The updated properties panel configuration.
 */
export const getPropertiesPanelConfig = (propertiesPanel, selection, nodes, mode, _mockMode) => {
  const nodeId = selection.nodes[0]
  const node = nodeId ? nodes[nodeId] : null

  const isVisible = propertiesPanel.expanded && selection.nodes.length === 1 && node && node.type !== 'temporary' // prevents opening panel on temporary node


  const schemaData = isVisible ? node?.data?.schema : null

  const disabled = mode !== 'edit' && mode !== 'paused'
  const currentNodeUid = isVisible ? node?.node_uid : null
  const nodeDataForm = isVisible ? node?.data?.form : null
  const nodeDataCases = isVisible ? node?.data?.cases : null
  const nodeDataDefaultTarget = isVisible ? node?.data?.default_target : null
  const nodeDataConditions = isVisible ? node?.data?.conditions : null

  // Return cached config if nothing meaningful changed to prevent unnecessary re-renders
  // Include nodeDataForm so external changes (case builder, undo/redo) propagate
  if (
    _prevConfig &&
    _prevExpanded === isVisible &&
    _prevNodeUid === currentNodeUid &&
    _prevSchemaData === schemaData &&
    _prevDisabled === disabled &&
    _prevNodeDataForm === nodeDataForm &&
    _prevNodeDataCases === nodeDataCases &&
    _prevNodeDataDefaultTarget === nodeDataDefaultTarget &&
    _prevNodeDataConditions === nodeDataConditions
  ) {
    return _prevConfig
  }

  _prevExpanded = isVisible
  _prevNodeUid = currentNodeUid
  _prevSchemaData = schemaData
  _prevDisabled = disabled
  _prevNodeDataForm = nodeDataForm
  _prevNodeDataCases = nodeDataCases
  _prevNodeDataDefaultTarget = nodeDataDefaultTarget
  _prevNodeDataConditions = nodeDataConditions

  _prevConfig = {
    expanded: isVisible,
    node: isVisible ? node : null,
    disabled
  }

  return _prevConfig
}
